import React from 'react';
import axios from 'axios';

// Msg #774 — Moderator/Admin duration picker for /kick and /ban.
// action = 'kick' → durations are session-only or short suspensions.
// action = 'ban'  → same durations but forwarded to /admin/ban (permanent
//                    if the "Permanent" option is chosen).
const KICK_DURATIONS = [
  { label: 'Kick (session only)', minutes: 0 },
  { label: '1 minute',            minutes: 1 },
  { label: '1 hour',              minutes: 60 },
  { label: '1 day',               minutes: 60 * 24 },
  { label: '1 week',              minutes: 60 * 24 * 7 },
  { label: '2 weeks',             minutes: 60 * 24 * 14 },
  { label: '1 month',             minutes: 60 * 24 * 30 },
  { label: '2 months',            minutes: 60 * 24 * 60 },
];

const BAN_DURATIONS = [
  ...KICK_DURATIONS.slice(1), // drop the "session only" for /ban
  { label: '1 year',      minutes: 60 * 24 * 365 },
  { label: 'PERMANENT',   minutes: 0 }, // 0 for /ban means permanent
];

export const KickDurationModal = ({ open, target, action, apiBase, requester, onDone, onClose }) => {
  const [selectedIdx, setSelectedIdx] = React.useState(0);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (open) { setSelectedIdx(0); setError(''); }
  }, [open, target, action]);

  if (!open) return null;

  const options = action === 'ban' ? BAN_DURATIONS : KICK_DURATIONS;
  const title = action === 'ban' ? `Ban ${target}` : `Kick ${target}`;

  const submit = async () => {
    const opt = options[selectedIdx];
    setSending(true); setError('');
    try {
      const url = action === 'ban' ? `${apiBase}/admin/ban` : `${apiBase}/admin/kick`;
      await axios.post(url, {
        requester, target,
        duration_minutes: opt.minutes,
      });
      if (onDone) onDone(opt);
      onClose();
    } catch (err) {
      const detail = err && err.response && err.response.data && err.response.data.detail;
      setError(detail || 'Action failed.');
      setSending(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(20,40,20,0.55)',
        zIndex: 1100, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 380, maxWidth: '92vw',
          background: '#d6e8c5',
          border: '2px solid #2d6b1e',
          boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
          padding: '20px 24px',
          fontFamily: 'Arial, Helvetica, sans-serif',
          color: '#1c3f1c',
        }}
        data-testid="kick-duration-modal"
      >
        <div
          style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 14, marginBottom: 12 }}
          data-testid="kick-duration-title"
        >
          {title}
        </div>
        <div style={{ fontSize: 12, marginBottom: 8 }}>Select a duration:</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
          {options.map((opt, i) => (
            <label
              key={opt.label}
              data-testid={`kick-duration-opt-${i}`}
              style={{
                fontSize: 12, cursor: 'pointer',
                padding: '4px 8px',
                background: selectedIdx === i ? '#cce0b0' : 'transparent',
                border: '1px solid ' + (selectedIdx === i ? '#2d6b1e' : 'transparent'),
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <input
                type="radio"
                name="kick-duration"
                checked={selectedIdx === i}
                onChange={() => setSelectedIdx(i)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {error && (
          <div
            data-testid="kick-duration-error"
            style={{ color: '#a02020', fontSize: 11, marginBottom: 8 }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            data-testid="kick-duration-cancel"
            onClick={onClose}
            disabled={sending}
            style={{
              background: '#e9f3da',
              border: '1px solid #5b8c47',
              padding: '4px 14px',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            data-testid="kick-duration-submit"
            onClick={submit}
            disabled={sending}
            style={{
              background: action === 'ban' ? '#7a1f1f' : '#1c3a1c',
              color: '#ecfcd9',
              border: '1px solid #2d6b1e',
              padding: '4px 14px',
              fontSize: 12,
              fontWeight: 'bold',
              cursor: sending ? 'wait' : 'pointer',
            }}
          >
            {sending ? '…' : (action === 'ban' ? 'BAN' : 'KICK')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KickDurationModal;
